import { DocumentData, getDocs, QueryDocumentSnapshot } from 'firebase/firestore';
import React from 'react';
import styled from 'styled-components';
import { getQuery, OrderItem } from 'services';
import toast from 'react-hot-toast';
import { WList } from 'shared/Components';
import { useHistory, useParams } from 'react-router-dom';
import { DashboardRoutePath } from 'modules/dashboard/navigation';

import { Box, Row } from '../../../../shared/Typhography';

const Container = styled.div`
  margin-top: 150px;
`;

interface RevenueArtist extends DocumentData {
  artist: number;
  company: number;
  total: number;
  id: string;
  name: string;
  profileUrl: string;
}

interface RevenueRowProps {
  data: QueryDocumentSnapshot<RevenueArtist>;
}

const OrderingCarrot = styled.span<{ direction: 'asc' | 'desc' }>`
  display: inline-block;
  transform: rotate(${({ direction }) => (direction == 'asc' ? '90deg' : '-90deg')});
`;

const RevenueRow = ({ data }: RevenueRowProps) => {
  const history = useHistory();

  if (Object.keys(data.data()).length == 0) {
    return <Row style={{ height: '64px' }}>CORRUPTED</Row>;
  }

  return (
    <Row style={{ height: '64px' }}>
      <span style={{ flex: 0.5 }}>
        <img src={data.data().profileUrl} style={{ height: '48px', width: '48px' }} />
      </span>
      <span style={{ flex: 1.5 }}>{data.data().name}</span>
      <span style={{ flex: 1 }}>${data.data().artist?.toFixed(5)}</span>
      <span style={{ flex: 1 }}>${data.data().total?.toFixed(5)}</span>
      <span
        style={{ flex: 1 }}
        onClick={() => history.push(`/${DashboardRoutePath.REVENUE_BY_ARTIST}/${data?.data().id}`)}
      >
        ARTIST REVENUE
      </span>
    </Row>
  );
};

export default function RevenueArtistScreen() {
  const { date } = useParams<{ date: string }>();
  const [data, setData] = React.useState<QueryDocumentSnapshot<RevenueArtist>[]>([]);
  const [isLoading, setLoading] = React.useState<boolean>(true);
  const [ordering, setOrdering] = React.useState<OrderItem[]>([]);

  const manageOrdering = (attribute: string) => {
    const orderingItem = ordering.find((item) => item.field === attribute);

    if (orderingItem) {
      if (orderingItem.type === 'asc')
        setOrdering(
          ordering.map((item) => (item?.field === attribute ? { ...item, type: 'desc' } : item)),
        );
      else setOrdering(ordering.filter((item) => item?.field !== attribute));

      return;
    }

    setOrdering([...ordering, { field: attribute, type: 'asc' }]);
  };

  const load = async (reset = false) => {
    setLoading(true);
    try {
      const nextQuery = getQuery(
        `revenue/${date}/artists`,
        [],
        ordering,
        data.length > 0 && !reset ? data.pop() : undefined,
        20,
      );
      const nextData = await getDocs(nextQuery);
      nextData.docs;
      setData(
        reset
          ? (nextData.docs as QueryDocumentSnapshot<RevenueArtist>[])
          : [...data, ...(nextData.docs as QueryDocumentSnapshot<RevenueArtist>[])],
      );
    } catch (err) {
      toast.error('Failed to fetch');
      console.log(err);
    }
    setLoading(false);
  };

  /* Initial data load */
  React.useEffect(() => {
    load(true);
  }, [ordering]);

  return (
    <Container>
      <Box>
        <Row style={{ height: '64px' }}>
          <span style={{ flex: 0.5, fontWeight: 600 }}>Artist Image</span>
          <span style={{ flex: 1.5, fontWeight: 600 }} onClick={() => manageOrdering('name')}>
            Artist Name{' '}
            {ordering?.find((oitem) => oitem?.field === 'name') && (
              <OrderingCarrot
                direction={ordering?.find((oitem) => oitem?.field === 'name')?.type ?? 'asc'}
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          <span style={{ flex: 1, fontWeight: 600 }} onClick={() => manageOrdering('artist')}>
            Artist Royalty{' '}
            {ordering?.find((oitem) => oitem?.field === 'artist') && (
              <OrderingCarrot
                direction={ordering?.find((oitem) => oitem?.field === 'artist')?.type ?? 'asc'}
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          <span style={{ flex: 1, fontWeight: 600 }} onClick={() => manageOrdering('total')}>
            Total{' '}
            {ordering?.find((oitem) => oitem?.field === 'total') && (
              <OrderingCarrot
                direction={ordering?.find((oitem) => oitem?.field === 'total')?.type ?? 'asc'}
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          <span style={{ flex: 1, fontWeight: 600 }}>Details</span>
        </Row>
        <WList
          data={data}
          maxHeight={window.innerHeight - 300}
          itemHeight={64}
          itemComponent={RevenueRow}
          scrollConfig={{
            fromBottom: 100,
            event: async () => {
              if (!isLoading) load();
            },
          }}
        />
      </Box>
    </Container>
  );
}
